import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { ArrowLeft, Target, Users, Heart, Lightbulb, Globe } from "lucide-react";

interface AboutProps {
  onBackToLanding: () => void;
}

const values = [
  {
    icon: Target,
    title: "Evidence-Based",
    description: "Every feature is grounded in research from cognitive science and decision theory."
  },
  {
    icon: Users,
    title: "User-Centered",
    description: "We build for real people making real decisions, not abstract theoretical frameworks."
  },
  {
    icon: Heart,
    title: "Empathetic",
    description: "Decision-making is deeply personal. We treat your choices with care and respect."
  },
  {
    icon: Lightbulb,
    title: "Continuous Learning",
    description: "We believe everyone can improve their judgment through practice and reflection."
  }
];

const milestones = [
  { year: "2023", event: "Mockups for user feedback in Obsidian.md and DataView" },
  { year: "2024", event: "No Code prototypes in Glide" },
  { year: "2025", event: "Entered 'Lovable Shipped' Season 1 program " },
  { year: "2025", event: "Official launch of Decisionr.xyz platform" }
];

export function About({ onBackToLanding }: AboutProps) {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBackToLanding}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Home</span>
              </Button>
              <div className="h-6 w-px bg-border" />
              <div>
                <h1 className="text-2xl font-semibold">About Decisionr.xyz</h1>
                <p className="text-sm text-muted-foreground">
                  Our mission, team, and vision for better decision making
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-medium mb-6">
            Making Better Decisions, One Choice at a Time
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Decisionr.xyz was born from a simple observation: we make thousands of decisions every day, 
            yet most of us never systematically learn from them. We're here to change that by making 
            decision science accessible, practical, and engaging for everyone.
          </p>
        </div>

        {/* Mission Statement */}
        <Card className="mb-16 bg-primary/5 border-primary/20">
          <CardContent className="p-8 text-center">
            <Globe className="h-12 w-12 text-primary mx-auto mb-6" />
            <h3 className="text-2xl font-medium mb-4">Our Mission</h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              To democratize decision science by providing tools that help individuals and teams 
              make better choices through systematic reflection, pattern recognition, and continuous learning.
            </p>
          </CardContent>
        </Card>

        {/* The Problem We're Solving */}
        <div className="mb-16">
          <h3 className="text-2xl font-medium mb-8 text-center">The Problem We're Solving</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader>
                <CardTitle className="text-orange-800">The Challenge</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-orange-900">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                    Most people don't track their decisions systematically
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                    We repeat the same decision-making mistakes
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                    Cognitive biases operate unconsciously
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                    Decision science research stays in academic journals
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-green-800">Our Solution</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-green-900">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                    Simple, structured decision journaling
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                    Pattern recognition through analytics
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                    Bias detection and awareness tools
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                    Research-backed, practical guidance
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Our Values */}
        <div className="mb-16">
          <h3 className="text-2xl font-medium mb-8 text-center">Our Values</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="text-center hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-lg flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-lg">{value.title}</CardTitle>
                  <CardDescription className="leading-relaxed">
                    {value.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>

        {/* Company Timeline */}
        <div className="mb-16">
          <h3 className="text-2xl font-medium mb-8 text-center">Our Journey</h3>
          <Card>
            <CardContent className="p-8">
              <div className="space-y-6">
                {milestones.map((milestone, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-medium">
                      {milestone.year}
                    </div>
                    <div className="flex-1">
                      <p className="text-muted-foreground">{milestone.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>



        {/* Vision for the Future */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-medium mb-4">Our Vision</h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
              We envision a world where everyone has the tools and knowledge to make thoughtful, 
              well-informed decisions. Where people learn from their choices, recognize their patterns, 
              and continuously improve their judgment.
            </p>
            <p className="text-muted-foreground">
              Every decision is an opportunity to learn. Every reflection is a step toward wisdom.
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16">
        <div className="container py-8">
          <div className="text-center text-sm text-muted-foreground">
            <p>© 2025 Decisionr.xyz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}